import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Settings, Smartphone, Wifi, Shield, MapPin, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';

type Specification = { label: string; value: string };

type Project = {
  title: string;
  category: string;
  description: string;
  image: string;
  features: string[];
  specifications: Specification[];
  useCases: string[];
  technologies: string[];
  warnings?: string[];
  androidAppUrl?: string;
  detailedUseCases?: { title: string; description: string }[];
};

export function ProjectDetail() {
  const { slug } = useParams();

  const projects: Record<string, Project> = {
    'frigolive': {
      title: 'Frigolive F-007',
      category: 'IoT & Sensörler',
      description: 'Araç ve lojistik sektörü için geliştirilmiş, GSM tabanlı canlı konum, sıcaklık, nem, ışık ve darbe verilerini izleyerek lojistik ve hassas ürün transferi süresince ürün bütünlüğünü ve güvenliğini garanti altına alın.',
      image: '/frigolive-2.png',
      features: [
        'Canlı Sıcaklık Takibi',
        'Hücresel Bağlantı',
        'Canlı Konum Takibi',
        'Web Tabanlı Raporlama',
        'Uzun Batarya Ömrü',
        'Mobil Uygulama Desteği',
        'Su Geçirmez Tasarım',
        'Kolay Kullanım'
      ],
      specifications: [
        // Bağlantı
        //{ label: 'Hücresel Bağlantı', value: 'EMEA+APAC' },
        { label: 'İletişim Aralığı', value: '5 dakika - 24 saat (seçilebilir aralık)' },
        //{ label: 'LTE Bantları', value: 'LTE-FDD: B1 / B3 / B5 / B7 / B8 / B18 / B19 / B20 / B26 / B28LTE-TDD: B40 / B41' },
        
        // Konum
        { label: 'Konumlama Yöntemi', value: 'GPS, WiFi, Hücresel Üçgenleme' },
        { label: 'Konum Doğruluğu', value: 'GPS: 10 m, WiFi: 10 m, Hücresel Üçgenleme: 500 m' },
        
        // Sensörler
        { label: 'Sensörler', value: 'Sıcaklık, Nem, Işık, Darbe' },
        { label: 'Ölçüm Kapasitesi', value: '30.000 kayda kadar' },
        { label: 'Kalibrasyon', value: 'ISO/IEC 17025 akredite, NIST izlenebilir' },
        
        // Sıcaklık ve Nem
        { label: 'Çalışma Sıcaklığı Aralığı', value: '-25°C ile +60°C' },
        { label: 'Nem Aralığı', value: '0%-100% RH' },
        { label: 'Sıcaklık Hassasiyeti', value: '±0.1°C' },
        { label: 'Nem Hassasiyeti', value: '±2% RH' },
        { label: 'Sıcaklık Sürüklenmesi', value: 'Yılda <0.03°C' },
        { label: 'Nem Sürüklenmesi', value: 'Yılda %0.25' },
        { label: 'Düşük Sıcaklık Desteği', value: '-30°C\'a kadar çalışma için tasarlanmıştır' },
        
        // Batarya
        { label: 'Batarya Kapasitesi', value: 'Li-ion 2800mAh / 10.08 Wh' },
        { label: 'Batarya Ömrü', value: '30+ Gün (60 dk iletim), 150+ Gün (24 Saat iletim)' },
        
        // Genel
        { label: 'Boyutlar', value: '108 x 62 x 24 mm' },
        { label: 'Ağırlık', value: '110 gram' },
        { label: 'Koruma', value: 'IP67 (su ve toz geçirmez)' },
        { label: 'Raf Ömrü', value: '1 yıl' }
      ],
      useCases: [
        'İlaç ve aşı taşımacılığı',
        'Gıda lojistiği',
        'Laboratuvar örnekleri',
        'Soğuk zincir yönetimi',
        'Endüstriyel sıcaklık takibi',
        'Sera ve tarım ürünleri'
      ],
      technologies: ['GSM/GPRS', 'Temperature Sensors', 'Cloud Platform', 'Web Dashboard']
    },
    'shalteron': {
      title: 'ShalterOn',
      category: 'Otomasyon',
      description: '6 röle kontrolü ve 4 dijital input ile gelişmiş otomasyon sistemi. Bahçe sulama, sera kontrolü ve endüstriyel otomasyon için ideal çözüm. Mobil uygulama ile uzaktan kontrol imkanı sunar.',
      image: '/gsm_role.png',
      features: [
        '6 adet röle çıkışı (10A/250V)',
        '4 adet dijital input',
        'Mobil uygulama kontrolü',
        'Zamanlama ve programlama',
        'M2M SIM Kart',
        'Durum raporlama'
      ],
      specifications: [
        { label: 'Bağlanabilir Cihaz Sayısı', value: '6 adet - NO/COM/NC' },
        { label: 'Kontrol Kapasitesi', value: '10A 250VAC | 10A 30VDC' },
        { label: 'Digital Input', value: '4 adet (Enerji vermeyin!)' },
        { label: 'Bağlantı', value: 'M2M SIM Kart | Hücresel Veri 10MB/Ay' },
        { label: 'Anten', value: '3M'},
        { label: 'Güç Kaynağı', value: '12V DC 1A Adaptör' },
        { label: 'Boyutlar', value: 'RT 207 Ray Tipi Kutu' },
        { label: 'Koruma', value: 'IP54' },
        { label: 'Buzzer', value: '1 adet'}
      ],
      useCases: [
        'Bahçe sulama sistemleri',
        'Sera otomasyon',
        'Endüstriyel ekipman kontrolü',
        'Aydınlatma kontrolü',
        'Motor kontrol sistemleri',
        'Alarm sistemleri'
      ],
      technologies: ['GSM/GPRS', 'IoT', 'Mobile App', 'SMS Gateway', 'M2M SIM Kart'],
      warnings: [
        '10 Amperden yüksek güçler için kontaktör kullanın.',
        'Okuma girişlerine 12V\'dan fazla enerji vermeyin!',
        'Cihaz girişleri kuru kontaktır. Enerji girişi yapmayın!',
        'Cihaz kurulumu için tecrübeli bir teknik servisten destek alın.'
      ]
    },
    'akilli-gecis-sistemi': {
      title: 'Akıllı Geçiş Sistemi',
      category: 'Güvenlik Sistemleri',
      description: 'QR kod ve mobil uygulama entegrasyonlu modern erişim kontrol sistemi. Okul, ofis ve fabrika girişleri için yoklama ve detaylı raporlama özellikleri sunar.',
      image: '/kgs_1.png',
      features: [
        'Dinamik QR kod ile giriş',
        'RFID kart desteği',
        'Mobil uygulama entegrasyonu',
        'Yoklama sistemi',
        'Detaylı raporlama',
        'Zaman dilimi kontrolü'
      ],
      specifications: [
        { label: 'Kullanıcı Kapasitesi', value: '20.000 kişi'},
        { label: 'Kayıt Kapasitesi', value: '100.000 kayıt'},
        { label: 'Tanımlama Yöntemleri', value: 'Proximity kart, Mifare kart, Mobil barkod'},
        { label: 'Kart Tipi', value: 'RFID 13.56MHz, QR Code' },
        { label: 'Okuma Mesafesi', value: '5-10 cm (kart) , barkod okuyucu destekli' },
        { label: 'Ekran', value: '2.4" Renkli TFT Ekran' },
        { label: 'Bağlantı', value: 'Ethernet / WiFi / RS-485 / USB' },
        { label: 'Güç', value: '12V DC 500mA' },
        { label: 'Boyutlar', value: '200x150x40 mm' },
        { label: 'Ek Fonksiyonlar', value: 'Anti-pass Back, Zaman Dilimi Tanımlama, Uyku Modu' }
      ],
      useCases: [
        'Okul Giriş Kontrolü',
        'Ofis Personel Takibi',
        'Fabrika Vardiya Kontrolü',
        'Etkinlik Katılımcı Takibi',
        'Kütüphane Geçiş Sistemi',
        'Hastane Ziyaretçi Sistemi'
      ],
      detailedUseCases: [
        {
          title: 'Turnike Geçiş Sistemleri',
          description:
            'İlimera PDKS cihazı, personel ve öğrencilerin turnikeden güvenli ve kontrollü geçişini sağlar. Kart veya mobil barkod okutulduğunda turnike otomatik açılır, yoğun saatlerde bile hızlı ve düzenli geçiş imkânı sunar. Tüm geçişler anlık olarak sisteme kaydedilir ve istenildiğinde detaylı rapor alınabilir. Bu sayede kurumlarda güvenlik ve devam kontrolü kolayca sağlanır.'
        },
        {
          title: 'Kapı Geçiş Sistemleri',
          description:
            'İlimera PDKS cihazı, kurumlarda kapı giriş-çıkış noktalarında güvenli ve kontrollü erişim sağlar. Yetkilendirilmiş personel veya öğrenciler kart veya mobil barkod ile kolayca giriş yapabilir. Yetkisiz erişim denemeleri ise cihaz tarafından engellenir; sesli ve görsel uyarılar devreye girer ve sistem yöneticisine bildirim gönderilir. Tüm giriş-çıkışlar anlık kaydedilir ve detaylı raporlar oluşturularak kurum güvenliği ve takip süreçleri güçlendirilir.'
        },
        {
          title: 'Yemekhane Kullanımı',
          description:
            'İlimera PDKS cihazı, yemekhane girişlerinde personel ve öğrencilerin yemek haklarını hızlı ve güvenli şekilde kontrol eder. Kart veya mobil barkod ile geçiş yapılabilir; sistem yalnızca yetkili kişilerin girişine izin verir. Bu sayede yemek israfı ve yetkisiz kullanım önlenir. Tüm girişler anlık kaydedilir ve yöneticiler detaylı raporlara ulaşabilir. Yemekhane kullanımı böylece düzenli, denetlenebilir ve adil şekilde yönetilir.'
        },
        {
          title: 'Toplantı Odası Geçiş Kontrolü',
          description:
            'İlimera PDKS cihazı, toplantı odalarına yalnızca yetkili personelin girişini sağlar. Kart veya mobil barkod ile erişim sağlanır, yetkisiz giriş denemeleri engellenir ve gerekirse alarm veya uyarı gönderilir. Tüm giriş-çıkışlar kaydedilir; hangi personelin hangi saatlerde odayı kullandığı raporlanabilir. Böylece güvenlik artırılır ve kurum içi kaynaklar verimli şekilde yönetilir.'
        },
        {
          title: 'Kütüphane Geçiş Kontrolü',
          description:
            'İlimera PDKS cihazı, kütüphane girişlerinde üyelerin güvenli ve düzenli erişimini sağlar. Kart veya mobil barkod ile giriş yapılabilir; yalnızca kayıtlı kullanıcılar erişebilir, yetkisiz girişler engellenir. Kapasite sınırlaması sayesinde kütüphanedeki kişi sayısı kontrol edilir. Rezervasyon özelliği ile önceden giriş hakkı tanımlanabilir ve kullanım verimliliği artar. Tüm giriş-çıkışlar kaydedilir ve yöneticilere raporlama imkânı sunulur.'
        },
        {
          title: 'Etkinlik Geçiş Kontrolü',
          description:
            'İlimera PDKS cihazı, konser, fuar, seminer ve benzeri etkinliklerde hızlı ve güvenli giriş sağlar. Katılımcılar bilet veya mobil QR kod ile giriş yapabilir; uzun kuyruklar önlenir ve düzenli geçiş sağlanır. Tüm girişler anlık kaydedilir, katılımcı sayısı takip edilir ve kapasite kontrolü yapılır. Yetkisiz girişler engellenerek güvenlik üst seviyede tutulur.'
        },
        {
          title: 'Hızlı Kasa Geçiş Kontrolü',
          description:
            'İlimera PDKS cihazı, market, mağaza ve self-servis alanlarında müşterilerin kasa işlemlerini hızlı ve güvenli şekilde tamamlamasını sağlar. Alışveriş fişi veya mobil QR kod ile geçiş yapılabilir; yoğun saatlerde kuyruklar önlenir ve müşteri memnuniyeti artar. Tüm geçişler kaydedilir, güvenlik ve satış takibi kolaylaşır. Günlük, haftalık veya aylık raporlarla işlem yoğunluğu analiz edilebilir.'
        }
      ],
      technologies: ['RFID', 'QR Code', 'TCP/IP', 'Mobile App', 'Cloud Database']
    },
    'depo-takip-sistemi': {
      title: 'Depo Takip Sistemi',
      category: 'Lojistik Çözümleri',
      description: 'Forklift ve depo operasyonları için geliştirilmiş anlık takip sistemi. Sensör tabanlı konum belirleme ve kullanıcı yetkilendirme sistemi ile depo verimliliğini artırır.',
      image: 'https://images.unsplash.com/photo-1672552226380-486fe900b322?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBmb3JrbGlmdCUyMHRyYWNraW5nfGVufDF8fHx8MTc1Njg4MzQwOHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      features: [
        'Anlık Forklift Konumu',
        'Çalışma Süresi Takibi',
        'Kullanıcı Kimlik Doğrulama',
        'Rota Optimizasyonu',
        'Performans Analizi',
        'Güvenlik Alarm Sistemi'
      ],
      specifications: [
        { label: 'Konum Hassasiyeti', value: '±1 metre' },
        { label: 'Güncelleme Sıklığı', value: '1 saniye' },
        { label: 'Bağlantı', value: 'Wi-Fi, Bluetooth' },
        { label: 'Batarya Ömrü', value: '8-12 saat' },
        { label: 'Çalışma Sıcaklığı', value: '-20°C ile +60°C' },
        { label: 'Koruma', value: 'IP67' }
      ],
      useCases: [
        'Depo Forklift Takibi',
        'Operasyon Verimliliği',
        'Güvenlik Monitoring',
        'Maliyet Optimizasyonu',
        'İş Sağlığı ve Güvenliği'
      ],
      technologies: ['GPS/GNSS', 'Wi-Fi', 'IoT Sensors', 'Real-time Analytics']
    },
    'sanal-capa-projesi': {
      title: 'Sanal Çapa Projesi',
      category: 'Denizcilik Teknolojisi',
      description: 'Denizcilik sektörü için geliştirilen innovatif çözüm. Fiziksel çapaya ihtiyaç olmadan teknenin GPS teknolojisi ile sabit konumda kalmasını sağlar.',
      image: 'https://images.unsplash.com/photo-1738339439832-860548f03cc4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxib2F0JTIwYW5jaG9yJTIwbWFyaW5lJTIwdGVjaG5vbG9neXxlbnwxfHx8fDE3NTY4ODM0MDh8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
      features: [
        'GPS Tabanlı Sabit Durma',
        'Otomatik Konum Düzeltme',
        'Mobil Uygulama Kontrolü',
        'Kumanda ile Yön Kontrolü',
        'Planlı Rota Sistemi',
        'Acil Durum Modu'
      ],
      specifications: [
        { label: 'GPS Hassasiyeti', value: '±2 metre' },
        { label: 'Çalışma Süresi', value: '6-8 saat' },
        { label: 'Kontrol Mesafesi', value: '500 metre' },
        { label: 'Su Geçirmezlik', value: 'IPX8' },
        { label: 'Tekne Boyutu', value: '8-15 metre' },
        { label: 'Deniz Durumu', value: '4 deniz miline kadar' }
      ],
      useCases: [
        'Tekne Demirleme',
        'Balıkçı Tekneleri',
        'Yat ve Yelkenli Tekneler',
        'Arama Kurtarma',
        'Bilimsel Araştırma Tekneleri'
      ],
      technologies: ['GPS/GNSS', 'Marine Electronics', 'Wireless Control', 'Mobile App']
    }
  };

  const project = projects[slug as keyof typeof projects];

  if (!project) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Proje Bulunamadı</h1>
        <p className="text-gray-600 mb-8">Aradığınız proje mevcut değil.</p>
        <Link to="/urunler">
          <Button>
            <ArrowLeft className="mr-2" size={16} />
            Ürünlere Dön
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="py-12">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <Link to="/urunler" className="flex items-center text-primary hover:text-primary/90 transition-colors">
          <ArrowLeft className="mr-2" size={16} />
          Ürünlere Dön
        </Link>
      </div>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="mb-4 bg-muted text-primary">
              {project.category}
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              {project.title}
            </h1>
            <p className="text-lg text-muted leading-relaxed mb-8">
              {project.description}
            </p>
            <div className="flex flex-col space-y-6">
            
              {slug === 'frigolive' && (
                <Link to="/downloads/frigolive">
                  <Button size="lg" className="group self-start">
                    Mobil Uygulamayı Keşfedin
                  </Button>
                </Link>
              )}

              {slug === 'shalteron' && (
                <Link to="/downloads/shalteron">
                  <Button size="lg" className="group self-start">
                    Mobil Uygulamayı Keşfedin
                  </Button>
                </Link>
              )}
              
              {slug === 'akilli-gecis-sistemi' && (
                <Link to="/downloads/akilli-gecis-sistemi">
                  <Button size="lg" className="group self-start">
                    Mobil Uygulamayı Keşfedin
                  </Button>
                </Link>
              )}
            </div>
          </div>
          <div>
            {slug === 'shalteron' ? (
              <img
                src='/gsm_role.png'
                alt={project.title}
                className="w-full h-auto rounded-lg shadow-xl"
              />
            ) : (
              <ImageWithFallback
                src={project.image}
                alt={project.title}
                className="w-full h-auto rounded-lg shadow-xl"
              />
            )}
          </div>
        </div>
      </section>

      {/* Product Video (ShalterOn) */}
      {slug === 'shalteron' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="rounded-lg overflow-hidden shadow-xl bg-black">
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/35G4v2CRiKQ?rel=0"
                title="ShalterOn Tanıtım Videosu"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              ></iframe>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-3xl font-bold text-foreground mb-8">Özellikler</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {project.features.map((feature, index) => (
            <div key={index} className="flex items-center !bg-white dark:bg-white p-4 rounded-lg border">
              <CheckCircle className="text-primary mr-3 flex-shrink-0" size={20} />
              <span className="!text-muted dark:!text-white font-medium">{feature}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Technical Specifications */}
      {slug !== 'akilli-gecis-sistemi' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">Teknik Özellikler</h2>
          <Card>
            <CardContent className="p-6">
              {slug === 'frigolive' ? (
              <div className="space-y-6">
                {/* Bağlantı */}
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">Bağlantı</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.specifications.slice(0, 3).map((spec, index) => (
                      <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="font-medium text-primary">{spec.label}:</span>
                        <span className="text-muted-foreground">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Konum */}
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">Konum</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.specifications.slice(3, 5).map((spec, index) => (
                      <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="font-medium text-primary">{spec.label}:</span>
                        <span className="text-muted-foreground">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sensörler */}
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">Sensörler</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.specifications.slice(5, 8).map((spec, index) => (
                      <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="font-medium text-primary">{spec.label}:</span>
                        <span className="text-muted-foreground">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sıcaklık ve Nem */}
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">Sıcaklık ve Nem</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.specifications.slice(8, 15).map((spec, index) => (
                      <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="font-medium text-primary">{spec.label}:</span>
                        <span className="text-muted-foreground">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Batarya */}
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">Batarya</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.specifications.slice(15, 17).map((spec, index) => (
                      <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="font-medium text-primary">{spec.label}:</span>
                        <span className="text-muted-foreground">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Genel */}
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4 border-b border-gray-200 pb-2">Genel</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.specifications.slice(17).map((spec, index) => (
                      <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                        <span className="font-medium text-primary">{spec.label}:</span>
                        <span className="text-muted-foreground">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {project.specifications.map((spec, index) => (
                  <div key={index} className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="font-medium text-primary">{spec.label}:</span>
                    <span className="text-muted-foreground">{spec.value}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>
      )}

      {/* Package Contents - ShalterOn */}
      {slug === 'shalteron' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">Kutu İçeriği</h2>
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center p-4 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-primary/20">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <Settings className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">1 adet</p>
                    <p className="text-muted-foreground">ShalterOn Kontrol Cihazı</p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-primary/20">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <Wifi className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">1 adet</p>
                    <p className="text-muted-foreground">3M Anten</p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-white/50 dark:bg-gray-800/50 rounded-lg border border-primary/20">
                  <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">1 adet</p>
                    <p className="text-muted-foreground">12VDC 1A Adaptör</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Warnings */}
      {project.warnings && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">Kullanımdan Önce Dikkat Edilmesi Gerekenler</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {project.warnings.map((warning, index) => (
              <Card key={index} className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/20">
                <CardContent className="p-4">
                  <div className="flex items-start">
                    <div className="w-6 h-6 rounded-full mt-0.5 mr-3 flex-shrink-0 flex items-center justify-center shadow-md border border-orange-500">
                      <svg className="w-4 h-4 text-orange-500 animate-pulse" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-orange-800 dark:text-orange-200 font-medium">{warning}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Use Cases */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-3xl font-bold text-foreground mb-8">Kullanım Alanları</h2>
        {slug === 'akilli-gecis-sistemi' && project.detailedUseCases ? (
          <div className="space-y-6">
            {project.detailedUseCases.map((item, idx) => (
              <Card key={idx} className="border">
                <CardContent className="p-6">
                  {idx === 0 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          <li>Personel ve öğrencilerin turnikeden güvenli ve kontrollü geçişini sağlar.</li>
                          <li>Kart veya mobil QR kod ile geçiş imkânı sunar.</li>
                          <li>Yoğun saatlerde bile hızlı ve düzenli geçiş sağlar.</li>
                          <li>Tüm geçişler anlık olarak sisteme kaydedilir.</li>
                          <li>Yetkisiz girişler engellenir.</li>
                          <li>Detaylı rapor alınabilir.</li>
                          <li>Kurumlarda güvenlik ve devam kontrolünü kolaylaştırır.</li>
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/ticket-barriers-subway-entrance.jpg"
                          alt="Turnike Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 360 }}>
                        <img
                          src="/ticket-barriers-subway-entrance.jpg"
                          alt="Turnike Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ) : idx === 1 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          <li>Kurumlarda kapı giriş-çıkış noktalarında güvenli ve kontrollü erişim sağlar.</li>
                          <li>Yetkilendirilmiş personel veya öğrenciler kart veya mobil barkod ile giriş yapabilir.</li>
                          <li>Yetkisiz erişim denemeleri cihaz tarafından engellenir.</li>
                          <li>Sesli ve görsel uyarılar devreye girer.</li>
                          <li>Sistem yöneticisine bildirim gönderilir.</li>
                          <li>Tüm giriş-çıkışlar anlık olarak sisteme kaydedilir.</li>
                          <li>Detaylı raporlar oluşturularak güvenlik ve takip süreçleri güçlendirilir.</li>
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/21228.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/21228.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ) : idx === 2 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          <li>Yemekhane girişlerinde personel ve öğrencilerin yemek haklarını hızlı ve güvenli şekilde kontrol eder.</li>
                          <li>Kart veya mobil barkod ile geçiş imkânı sunar.</li>
                          <li>Yalnızca yetkili kişilerin girişine izin verir.</li>
                          <li>Yemek israfı ve yetkisiz kullanımın önüne geçer.</li>
                          <li>Tüm girişler anlık olarak sisteme kaydedilir.</li>
                          <li>Yöneticiler detaylı raporlara ulaşabilir.</li>
                          <li>Yemekhane kullanımı düzenli, denetlenebilir ve adil şekilde yönetilir.</li>
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/chef-preparing-recipe.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/chef-preparing-recipe.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ) : idx === 3 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          <li>Toplantı odalarına yalnızca yetkili personelin girişini sağlar.</li>
                          <li>Kart veya mobil barkod ile erişim imkânı sunar.</li>
                          <li>Yetkisiz giriş denemeleri engellenir.</li>
                          <li>Gerekirse alarm veya uyarı gönderilir.</li>
                          <li>Tüm giriş-çıkışlar kaydedilir.</li>
                          <li>Hangi personelin hangi saatlerde odayı kullandığı raporlanabilir.</li>
                          <li>Güvenlik artırılır ve kurum içi kaynaklar verimli şekilde yönetilir.</li>
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/empty-hallway-background.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/empty-hallway-background.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ): idx === 4 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          <li>Kütüphane girişlerinde üyelerin güvenli ve düzenli erişimini sağlar.</li>
                          <li>Kart veya mobil barkod ile giriş imkânı sunar.</li>
                          <li>Yalnızca kayıtlı kullanıcılar erişebilir; yetkisiz girişler engellenir.</li>
                          <li>Kapasite sınırlaması ile kütüphanedeki kişi sayısı kontrol edilir.</li>
                          <li>Rezervasyon özelliği ile önceden giriş hakkı tanımlanabilir, kullanım verimliliği artar.</li>
                          <li>Tüm giriş-çıkışlar kaydedilir.</li>
                          <li>Yöneticilere raporlama imkânı sunar.</li>
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/young-student-learning-library.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/young-student-learning-library.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ): idx === 5 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          <li>Konser, fuar, seminer ve benzeri etkinliklerde hızlı ve güvenli giriş sağlar.</li>
                          <li>Katılımcılar bilet veya mobil QR kod ile giriş yapabilir.</li>
                          <li>Uzun kuyruklar önlenir ve düzenli geçiş sağlanır.</li>
                          <li>Tüm girişler anlık kaydedilir.</li>
                          <li>Katılımcı sayısı takip edilir ve kapasite kontrolü yapılır.</li>
                          <li>Yetkisiz girişler engellenir, güvenlik üst seviyede tutulur.</li>
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/retro-theatre-scene-celebrate-world-theatre-day.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/retro-theatre-scene-celebrate-world-theatre-day.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110 cursor-zoom-in"
                        />
                      </div>
                    </div>
                  ): idx === 6 ? (
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1 basis-0 min-w-0">
                        <div className="flex items-start mb-3">
                          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                            <Settings className="text-primary" size={20} />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                        </div>
                        <ul className="text-muted leading-relaxed break-words list-disc pl-5 space-y-2">
                          <li>Market, mağaza ve self-servis alanlarında hızlı ve güvenli kasa geçişi sağlar.</li>
                          <li>Alışveriş fişi veya mobil QR kod ile geçiş yapılabilir.</li>
                          <li>Yoğun saatlerde kuyruklar önlenir, müşteri memnuniyeti artar.</li>
                          <li>Tüm geçişler kaydedilir; güvenlik ve satış takibi kolaylaşır.</li>
                          <li>Günlük, haftalık veya aylık raporlarla işlem yoğunluğu analiz edilebilir.</li>
                        </ul>
                      </div>
                      {/* Mobile image */}
                      <div className="group overflow-hidden rounded-md block md:hidden">
                        <img
                          src="/abstract-blur-shopping-mall-retails-store-interior-background.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-auto object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110"
                        />
                      </div>
                      {/* Desktop image */}
                      <div className="hidden md:block group overflow-hidden rounded-md" style={{ width: 533, height: 400 }}>
                        <img
                          src="/abstract-blur-shopping-mall-retails-store-interior-background.jpg"
                          alt="Kapı Geçiş Sistemleri"
                          className="block w-full h-full object-contain transform transition-transform duration-500 ease-out hover:scale-110 group-hover:scale-110"
                        />
                      </div>
                    </div>
                  ):(
                    <>
                      <div className="flex items-start mb-4">
                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                          <Settings className="text-primary" size={20} />
                        </div>
                        <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {project.useCases.map((useCase, index) => (
              <Card key={index} className="p-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mr-4">
                    <Settings className="text-primary" size={20} />
                  </div>
                  <span className="text-foreground font-medium">{useCase}</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
      
      {/* Product Documentation (ShalterOn) */}
      {slug === 'shalteron' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-8">Ürün Dokümantasyonu</h2>
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
            <CardContent className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">ShalterOn Kullanım Kılavuzu</h3>
                    <p className="text-muted-foreground">Detaylı kurulum ve kullanım bilgileri</p>
                  </div>
                </div>
                <Button 
                  onClick={() => window.open('/ShalterOn_v1.5.pdf', '_blank', 'noopener,noreferrer')}
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  PDF'i Görüntüle
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      )}
      
      {/* Technologies */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-3xl font-bold text-foreground mb-8">Kullanılan Teknolojiler</h2>
        <div className="flex flex-wrap gap-3">
          {project.technologies.map((tech) => (
            <Badge key={tech} variant="outline" className="px-4 py-2 text-sm">
              {tech}
            </Badge>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Bu Proje Hakkında Daha Fazla Bilgi Almak İster misiniz?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Teknik detaylar, fiyat bilgisi ve kurulum süreci hakkında 
            uzman ekibimizden bilgi alın.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {slug === 'frigolive' && (
                <Button 
                  size="lg" 
                  className="group self-start"
                  onClick={() => window.open('https://frigolive.com/', '_blank', 'noopener,noreferrer')}
                >
                  Frigolive Web Sitesini Ziyaret Edin
                </Button>
              )}

            <Link to="/iletisim">
              <Button
                size="lg"
                variant="outline"
                className="dark:bg-gray-800 border-primary text-primary dark:hover:bg-gray-800 hover:bg-primary dark:hover:text-white"
              >
                İletişime Geçin
              </Button>
            </Link>

            <Link to="/urunler">
              <Button
                variant="outline"
                size="lg"
                className="dark:bg-gray-800 border-primary text-primary dark:hover:bg-gray-800 hover:bg-primary dark:hover:text-white">
                Diğer Projeleri İncele
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}